import type {
	CurrencyType,
	CustomDetailEnum,
	ExpectedTransactionType,
	InvestmentWorkflowType,
	TransactionTitle,
	TransactionType,
} from '@dealstack/prisma-client';

export enum HolderRole {
	buyer = 'buyer',
	seller = 'seller',
}

/** An issuing/funding entity as a document reads it. */
export interface SmartFieldsIssuer {
	name?: string | null;
	number?: string | null;
	address: {
		country?: string | null;
		city?: string | null;
		postCode?: string | null;
		street?: string | null;
		houseNumber?: string | null;
		singleAddressString?: string | null;
	};
}

/** One completed transaction on a security, as a document reads it. */
export interface SmartFieldsLastTransaction {
	id: string;
	date: Date;
	type: TransactionType | null;
	title: TransactionTitle | null;
	units?: number;
	cost?: number;
	holderRole: HolderRole;
}

/**
 * A security allocated in an investment still in flight. Sourced from
 * ShareAssociation, NOT from Transaction: while a MIP transaction is in progress
 * no Transaction row exists yet, so these numbers live only on the association.
 * Deliberately a sibling of `holdings` rather than part of it — an unexecuted
 * allocation has neither a balance nor a transaction, so folding it into
 * `holdings` would make existing aggregate bindings (`holdings | map: "unitsHeld"
 * | sum`) absorb zero-unit phantom rows.
 */
export interface SmartFieldsAllocation {
	security: {
		id: string;
		name: string;
		type: string;
		currency: string;
		nominalValue?: number | null;
		votesPerUnit?: number | null;
		issuer: SmartFieldsIssuer;
	};
	units?: number;
	cost?: number;
	expectedType: ExpectedTransactionType | null;
	targetDate: Date | null;
	investmentType: InvestmentWorkflowType;
}

export interface SmartFieldsAvailableSources {
	currentDate: Date;
	deal: {
		projectRepresentativePerson: {
			positionInCompany?: string | null;
			signatureImageFileId?: string | null;
		};
	};
	mip: {
		name?: string | null;
		currency?: CurrencyType | null;
		entity: {
			name?: string | null;
			number?: string | null;
			address: {
				country?: string | null;
				city?: string | null;
				postCode?: string | null;
				street?: string | null;
				houseNumber?: string | null;
				singleAddressString?: string | null;
			};
		};
		bankAccount: {
			bankName?: string | null;
			country?: string | null;
			swiftCode?: string | null;
			accountHolder?: string | null;
			iban?: string | null;
			accountNumber?: string | null;
			otherDetails?: string | null;
		};
	};
	investor: {
		firstName: string | null;
		lastName: string | null;
		email: string;
		birthDate: Date | null;
		phoneNumber: string | null;
		telephonePrefix: string | null;
		address: {
			country?: string | null;
			city?: string | null;
			postCode?: string | null;
			street?: string | null;
			houseNumber?: string | null;
			singleAddressString?: string | null;
		};
		bankAccount: {
			bankName?: string | null;
			country?: string | null;
			swiftCode?: string | null;
			accountHolder?: string | null;
			iban?: string | null;
			accountNumber?: string | null;
			otherDetails?: string | null;
		};
		additionalDetails: {
			taxResidency?: string | null;
			tin?: string | null;
			countryOfBirth?: string | null;
			countryOfBirthName?: string | null;
			placeOfBirth?: string | null;
		};
		// Every tax residence held by the person, primary-with-TIN first. Unlike
		// `additionalDetails.taxResidency`/`tin` (a single value, gated on the
		// programme's custom details), this is ungated and zero-to-many.
		taxResidences: {
			countryCode?: string | null;
			country?: string | null;
			authority?: string | null;
			number?: string | null;
		}[];
		// Employment history, the primary record first.
		employment: {
			employerName?: string | null;
			employerNumber?: string | null;
			employerAddress: {
				country?: string | null;
				city?: string | null;
				postCode?: string | null;
				street?: string | null;
				houseNumber?: string | null;
				singleAddressString?: string | null;
			};
			jobTitle?: string | null;
			startDate?: Date | null;
			endDate?: Date | null;
			employeeReference?: string | null;
			isPrimary: boolean;
		}[];
		// Citizenships held, resolved from the stored country codes.
		nationalities: {
			countryCode?: string | null;
			country?: string | null;
			nationality?: string | null;
		}[];
		/**
		 * Cohorts (MipClassifier of type INVESTOR_COHORT) this investor belongs to, each with the
		 * tags grouping it. Keyed by MipClassifier.id for the same reason `schemes` is keyed by
		 * Tag.id: a condition pinned to a cohort must survive that cohort being renamed.
		 */
		cohorts: { id: string; name: string; tags: { name: string }[] }[];
		/**
		 * Schemes (Tag) this investor is classified under, each carrying only the
		 * cohorts THIS investor belongs to within that scheme. Bind a document to
		 * one scheme (`Tier`) and every recipient renders their own cohort
		 * (`Tier 3`). Keyed by Tag.id so renaming a scheme cannot break a bound
		 * document. Usually one cohort per scheme, but MipClassifierInvestor does
		 * not forbid two, so it is a list and renders joined.
		 */
		schemes: { id: string; name: string; cohorts: { name: string }[] }[];
		shareWithInvestorSecurityValuation: boolean;
	};
	vehicleData: {
		pivName?: string | null;
		corporateNumber?: string | null;
		address: {
			country?: string | null;
			city?: string | null;
			postCode?: string | null;
			street?: string | null;
			houseNumber?: string | null;
			singleAddressString?: string | null;
		};
	};
	representative: {
		firstName?: string | null;
		lastName?: string | null;
		email?: string;
		birthDate?: Date | null;
		phoneNumber?: string | null;
		telephonePrefix?: string | null;
		address: {
			country?: string | null;
			city?: string | null;
			postCode?: string | null;
			street?: string | null;
			houseNumber?: string | null;
			singleAddressString?: string | null;
		};
	};
	declarations: {
		isPoliticallyExposed: boolean;
		isBeneficialOwner: boolean;
		fundsSource: {
			salary: boolean;
			inheritance: boolean;
			investmentIncome: boolean;
			personalSavings: boolean;
			other: boolean;
		};
		/**
		 * Whether each declaration above has actually been answered.
		 *
		 * An unanswered declaration reads as `false` above, which is indistinguishable from a
		 * completed "no" — and for a rendered value that is long-standing behaviour nothing
		 * should change under it. A CONDITION cannot live with the conflation: "we know they are
		 * not a PEP" and "nobody has asked yet" must not put the same clause in a deed. So the
		 * fact is carried alongside rather than by widening the values, and the condition
		 * evaluator reads it to answer `unknown`.
		 */
		answered: {
			isPoliticallyExposed: boolean;
			isBeneficialOwner: boolean;
			fundsSource: {
				salary: boolean;
				inheritance: boolean;
				investmentIncome: boolean;
				personalSavings: boolean;
				other: boolean;
			};
		};
	};
	holdings: {
		unitsHeld?: number;
		fairMarketValue?: number;
		costOfAcquisition?: number;
		votes?: number;
		security: {
			id: string;
			name: string;
			type: string;
			currency: string;
			nominalValue?: number | null;
			votesPerUnit?: number | null;
			issuer: SmartFieldsIssuer;
		};
		/** The most recent completed transaction, or null when there is none.
		 * `transactions` below is the full history and stays unchanged. */
		lastTransaction: SmartFieldsLastTransaction | null;
		transactions: {
			date: Date;
			units?: number;
			cost?: number;
			holderRole: HolderRole;
		}[];
	}[];
	allocations: SmartFieldsAllocation[];
	loans: {
		totalValue?: number;
		funder: SmartFieldsIssuer;
	}[];
	customDetails?: {
		id?: string | null;
		question?: string | null;
		answer?: string | null;
		entityType?: CustomDetailEnum | null;
	}[];
}

export const SMART_FIELDS_SOURCES_MOCKED_DATA: SmartFieldsAvailableSources = {
	currentDate: new Date(),
	deal: {
		projectRepresentativePerson: {
			positionInCompany: 'General Manager',
			signatureImageFileId: 'mocked_file_id_1234567890',
		},
	},
	mip: {
		name: 'Example mip name',
		currency: 'USD',
		entity: {
			name: 'Entity Name',
			number: '123456789',
			address: {
				country: 'USA',
				city: 'New York',
				postCode: '10001',
				street: 'Main Street',
				houseNumber: '123',
				singleAddressString: '123 Main Street, New York, USA, 10001',
			},
		},
		bankAccount: {
			bankName: 'Example bank name',
			country: 'USA',
			swiftCode: 'EXAMPUS33XXX',
			accountHolder: 'Eleonora Doe',
			iban: 'US12345678901234567890',
			accountNumber: '9876543210',
			otherDetails: 'Routing number: 021000021',
		},
	},
	investor: {
		firstName: 'John',
		lastName: 'Doe',
		email: 'john.doe@example.com',
		birthDate: new Date('1980-01-01'),
		phoneNumber: '123456789',
		telephonePrefix: '+1',
		address: {
			country: 'USA',
			city: 'New York',
			postCode: '10001',
			street: '5th Avenue',
			houseNumber: '1',
			singleAddressString: '1, 5th Avenue, New York, 10001, USA',
		},
		bankAccount: {
			bankName: 'Best Bank',
			country: 'USA',
			swiftCode: 'BESTUS33',
			accountHolder: 'John Doe',
			iban: 'US12345678901234567890',
			accountNumber: '1234567890',
			otherDetails: 'Routing number: 021000021',
		},
		additionalDetails: {
			taxResidency: 'gb',
			tin: '123456789',
			countryOfBirth: 'gb',
			countryOfBirthName: 'United Kingdom',
			placeOfBirth: 'London',
		},
		taxResidences: [
			{
				countryCode: 'GB',
				country: 'United Kingdom',
				authority: 'HM Revenue & Customs',
				number: '123456789',
			},
			{
				countryCode: 'US',
				country: 'United States',
				authority: 'Internal Revenue Service',
				number: '987-65-4321',
			},
		],
		employment: [
			{
				employerName: 'Example Holdings Ltd',
				employerNumber: '01234567',
				employerAddress: {
					country: 'USA',
					city: 'New York',
					postCode: '10001',
					street: 'Main Street',
					houseNumber: '123',
					singleAddressString: '123 Main Street, New York, USA, 10001',
				},
				jobTitle: 'Chief Financial Officer',
				startDate: new Date('2019-04-01'),
				endDate: null,
				employeeReference: 'EMP-00123',
				isPrimary: true,
			},
			{
				employerName: 'Previous Employer Inc.',
				employerNumber: '07654321',
				employerAddress: {
					country: 'USA',
					city: 'Boston',
					postCode: '02108',
					street: 'Beacon Street',
					houseNumber: '45',
					singleAddressString: '45 Beacon Street, Boston, USA, 02108',
				},
				jobTitle: 'Finance Director',
				startDate: new Date('2014-09-15'),
				endDate: new Date('2019-03-31'),
				employeeReference: 'PE-991',
				isPrimary: false,
			},
		],
		nationalities: [
			{ countryCode: 'GB', country: 'United Kingdom', nationality: 'British' },
			{ countryCode: 'US', country: 'United States', nationality: 'American' },
		],
		cohorts: [
			{
				id: 'cohort-management-team',
				name: 'Management Team',
				tags: [{ name: 'Tranche A' }, { name: 'Vested' }, { name: 'Board' }],
			},
			{ id: 'cohort-advisors', name: 'Advisors', tags: [{ name: 'Tranche B' }] },
		],
		schemes: [
			{ id: 'tag-tier', name: 'Tier', cohorts: [{ name: 'Tier 3' }] },
			{ id: 'tag-region', name: 'Region', cohorts: [{ name: 'EMEA' }, { name: 'UK' }] },
		],
		shareWithInvestorSecurityValuation: true,
	},
	vehicleData: {
		pivName: 'Best Vehicle',
		corporateNumber: '123456789',
		address: {
			country: 'USA',
			city: 'New York',
			postCode: '10001',
			street: '5th Avenue',
			houseNumber: '1',
			singleAddressString: '1, 5th Avenue, New York, 10001, USA',
		},
	},
	representative: {
		firstName: 'Jane',
		lastName: 'Smith',
		email: 'jane.smith@example.com',
		birthDate: new Date('1990-02-02'),
		phoneNumber: '987654321',
		telephonePrefix: '+1',
		address: {
			country: 'USA',
			city: 'New York',
			postCode: '10001',
			street: '6th Avenue',
			houseNumber: '2',
			singleAddressString: '2, 6th Avenue, New York, 10001, USA',
		},
	},
	declarations: {
		isPoliticallyExposed: false,
		isBeneficialOwner: false,
		fundsSource: {
			salary: false,
			inheritance: false,
			investmentIncome: false,
			personalSavings: false,
			other: false,
		},
		// The sample investor has completed KYC, so every declaration is a real answer. A sample
		// with unanswered declarations would make the author's own preview refuse its conditions.
		answered: {
			isPoliticallyExposed: true,
			isBeneficialOwner: true,
			fundsSource: {
				salary: true,
				inheritance: true,
				investmentIncome: true,
				personalSavings: true,
				other: true,
			},
		},
	},
	holdings: [
		{
			security: {
				id: '00000000-0000-4000-8000-000000000001',
				name: 'Tech Corp Bond',
				type: 'Bond',
				currency: 'USD',
				nominalValue: 1,
				votesPerUnit: 1,
				issuer: {
					name: 'Tech Corp',
					number: '123456789',
					address: {
						country: 'USA',
						city: 'San Francisco',
						postCode: '94105',
						street: 'Market Street',
						houseNumber: '123',
						singleAddressString: '123 Market Street, San Francisco, CA 94105',
					},
				},
			},
			unitsHeld: 100,
			fairMarketValue: 400,
			costOfAcquisition: 5000,
			votes: 100,
			lastTransaction: {
				id: '00000000-0000-4000-8000-0000000000a1',
				date: new Date('2023-01-15'),
				type: 'ISSUE',
				title: 'BENEFICIAL_INTEREST_AND_LEGAL_TITLE',
				units: 50,
				cost: 2500,
				holderRole: HolderRole.buyer,
			},
			transactions: [
				{
					date: new Date('2023-01-15'),
					units: 50,
					cost: 2500,
					holderRole: HolderRole.buyer,
				},
			],
		},
		{
			security: {
				id: '00000000-0000-4000-8000-000000000002',
				name: 'Green Energy Stock',
				type: 'Stock',
				currency: 'EUR',
				nominalValue: 1,
				votesPerUnit: 0,
				issuer: {
					name: 'Green Energy Inc.',
					number: '987654321',
					address: {
						country: 'Germany',
						city: 'Berlin',
						postCode: '10117',
						street: 'Unter den Linden',
						houseNumber: '42',
						singleAddressString: '42 Unter den Linden, Berlin 10117',
					},
				},
			},
			unitsHeld: 200,
			fairMarketValue: 300,
			costOfAcquisition: 10000,
			votes: 200,
			lastTransaction: {
				id: '00000000-0000-4000-8000-0000000000a2',
				date: new Date('2023-02-10'),
				type: 'TRANSFER',
				title: 'BENEFICIAL_INTEREST_AND_LEGAL_TITLE',
				units: 100,
				cost: 5000,
				holderRole: HolderRole.buyer,
			},
			transactions: [
				{
					date: new Date('2023-02-10'),
					units: 100,
					cost: 5000,
					holderRole: HolderRole.buyer,
				},
				{
					date: new Date('2023-02-10'),
					units: 100,
					cost: 5000,
					holderRole: HolderRole.seller,
				},
			],
		},
	],
	allocations: [
		{
			security: {
				id: '00000000-0000-4000-8000-000000000001',
				name: 'Tech Corp Bond',
				type: 'Bond',
				currency: 'USD',
				nominalValue: 1,
				votesPerUnit: 1,
				issuer: {
					name: 'Tech Corp',
					number: '123456789',
					address: {
						country: 'USA',
						city: 'San Francisco',
						postCode: '94105',
						street: 'Market Street',
						houseNumber: '123',
						singleAddressString: '123 Market Street, San Francisco, CA 94105',
					},
				},
			},
			units: 250,
			cost: 12500,
			expectedType: 'ISSUE',
			targetDate: new Date('2026-04-01'),
			investmentType: 'ALLOCATION',
		},
		{
			security: {
				id: '00000000-0000-4000-8000-000000000003',
				name: 'Series C Preference',
				type: 'Preference',
				currency: 'USD',
				nominalValue: 1,
				votesPerUnit: 0,
				issuer: {
					name: 'Tech Corp',
					number: '123456789',
					address: {
						country: 'USA',
						city: 'San Francisco',
						postCode: '94105',
						street: 'Market Street',
						houseNumber: '123',
						singleAddressString: '123 Market Street, San Francisco, CA 94105',
					},
				},
			},
			units: 400,
			cost: 20000,
			expectedType: 'ISSUE',
			targetDate: new Date('2026-05-01'),
			investmentType: 'JOINER',
		},
	],
	loans: [
		{
			totalValue: 500,
			funder: {
				name: 'Tech Corp',
				number: '123456789',
				address: {
					country: 'USA',
					city: 'San Francisco',
					postCode: '94105',
					street: 'Market Street',
					houseNumber: '123',
					singleAddressString: '123 Market Street, San Francisco, CA 94105',
				},
			},
		},
		{
			totalValue: 300,
			funder: {
				name: 'Green Energy Inc.',
				number: '987654321',
				address: {
					country: 'Germany',
					city: 'Berlin',
					postCode: '10117',
					street: 'Unter den Linden',
					houseNumber: '42',
					singleAddressString: '42 Unter den Linden, Berlin 10117',
				},
			},
		},
	],
};

// A second preview dataset with deliberately long values in every text field
// (long names, long entity/bank names, very long address strings, an extra
// holding and loan). Used by the editor's "Preview PDF → Long values" option
// to stress-test wrapping, box overflow, and multi-line layout — the standard
// set above uses realistic short values.
export const SMART_FIELDS_SOURCES_MOCKED_DATA_LONG: SmartFieldsAvailableSources = {
	currentDate: new Date(),
	deal: {
		projectRepresentativePerson: {
			positionInCompany: 'Chief Executive Officer & Managing Director of Operations',
			signatureImageFileId: 'mocked_file_id_1234567890',
		},
	},
	mip: {
		name: 'Meridian Global Infrastructure & Renewable Energy Partnership Fund IV',
		currency: 'USD',
		entity: {
			name: 'Meridian Global Infrastructure Holdings International (Luxembourg) S.à r.l.',
			number: 'B-1234567890-LU-2024',
			address: {
				country: 'Luxembourg',
				city: 'Luxembourg City',
				postCode: 'L-2449',
				street: 'Boulevard Grande-Duchesse Charlotte',
				houseNumber: '124-128',
				singleAddressString:
					'124-128 Boulevard Grande-Duchesse Charlotte, L-2449 Luxembourg City, Grand Duchy of Luxembourg',
			},
		},
		bankAccount: {
			bankName: 'Banque Internationale à Luxembourg Société Anonyme',
			country: 'Luxembourg',
			swiftCode: 'BILLLULLXXX',
			accountHolder:
				'Meridian Global Infrastructure Holdings International (Luxembourg) S.à r.l.',
			iban: 'LU28 0019 4006 4475 0000',
			accountNumber: '0019400644750000',
			otherDetails:
				'Bank code: 001, Branch (Agencia): 1234, Account (Conta): 12345-6, Account type: CC',
		},
	},
	investor: {
		firstName: 'Alexandria-Josephine',
		lastName: 'Van Der Berg-Montgomery',
		email: 'alexandria.josephine.vandenberg.montgomery@investmentholdings-international.example.com',
		birthDate: new Date('1980-01-01'),
		phoneNumber: '5551234567890',
		telephonePrefix: '+352',
		address: {
			country: 'United Kingdom',
			city: 'Kingston upon Thames',
			postCode: 'KT1 2EE',
			street: 'Coombe Hill Road, Coombe Estate',
			houseNumber: '148A',
			singleAddressString:
				'148A Coombe Hill Road, Coombe Estate, Kingston upon Thames, Surrey, KT1 2EE, United Kingdom',
		},
		bankAccount: {
			bankName: 'The Royal Bank of Scotland International Limited',
			country: 'United Kingdom',
			swiftCode: 'RBOSGGSXXXX',
			accountHolder: 'Alexandria-Josephine Van Der Berg-Montgomery',
			iban: 'GB29 NWBK 6016 1331 9268 19',
			accountNumber: '31926819',
			otherDetails: 'Sort code: 60-16-13, Account number: 31926819',
		},
		additionalDetails: {
			taxResidency: 'gb',
			tin: 'GB-123456789-2024-TAX',
			countryOfBirth: 'gb',
			countryOfBirthName: 'United Kingdom of Great Britain and Northern Ireland',
			placeOfBirth: 'Kingston upon Thames, Royal Borough of Kingston, Greater London',
		},
		taxResidences: [
			{
				countryCode: 'GB',
				country: 'United Kingdom of Great Britain and Northern Ireland',
				authority: 'His Majesty’s Revenue & Customs — International Compliance Division',
				number: 'GB-123456789-2024-TAX',
			},
			{
				countryCode: 'LU',
				country: 'Grand Duchy of Luxembourg',
				authority: 'Administration des Contributions Directes du Grand-Duché de Luxembourg',
				number: 'LU-9876543210987-2024-TAX-RESIDENCE',
			},
		],
		employment: [
			{
				employerName:
					'Meridian Global Infrastructure Holdings International (Luxembourg) S.à r.l.',
				employerNumber: 'B-1234567890-LU-2024',
				employerAddress: {
					country: 'Luxembourg',
					city: 'Luxembourg City',
					postCode: 'L-2449',
					street: 'Boulevard Grande-Duchesse Charlotte',
					houseNumber: '124-128',
					singleAddressString:
						'124-128 Boulevard Grande-Duchesse Charlotte, L-2449 Luxembourg City, Grand Duchy of Luxembourg',
				},
				jobTitle: 'Chief Financial Officer & Head of International Treasury Operations',
				startDate: new Date('2019-04-01'),
				endDate: null,
				employeeReference: 'EMP-LU-2019-000123456',
				isPrimary: true,
			},
			{
				employerName:
					'Meridian Renewable Energy Partnership Holdings (Ireland) Designated Activity Company',
				employerNumber: 'IE-987654321-2024',
				employerAddress: {
					country: 'Ireland',
					city: 'Dublin',
					postCode: 'D02 XY45',
					street: 'Sir John Rogerson’s Quay, Grand Canal Dock',
					houseNumber: '70',
					singleAddressString:
						'70 Sir John Rogerson’s Quay, Grand Canal Dock, Dublin 2, D02 XY45, Ireland',
				},
				jobTitle: 'Group Finance Director, Renewable Infrastructure Division',
				startDate: new Date('2014-09-15'),
				endDate: new Date('2019-03-31'),
				employeeReference: 'IE-EMP-2014-0000991',
				isPrimary: false,
			},
		],
		nationalities: [
			{
				countryCode: 'GB',
				country: 'United Kingdom of Great Britain and Northern Ireland',
				nationality: 'British (United Kingdom of Great Britain and Northern Ireland)',
			},
			{
				countryCode: 'LU',
				country: 'Grand Duchy of Luxembourg',
				nationality: 'Luxembourgish (Grand Duchy of Luxembourg)',
			},
		],
		cohorts: [
			{
				id: 'cohort-senior-management-tranche-a',
				name: 'Senior Management Incentive Participants (Tranche A)',
				tags: [
					{ name: 'Long Term Incentive Plan 2024-2034' },
					{ name: 'Fully Vested Beneficial Interest' },
					{ name: 'Board-Approved Allocation' },
				],
			},
			{
				id: 'cohort-external-advisory-board',
				name: 'External Advisory Board Members and Consultants',
				tags: [{ name: 'Deferred Consideration Tranche B' }],
			},
		],
		schemes: [
			{
				id: 'tag-tier-long',
				name: 'Long Term Incentive Plan Participation Tier',
				cohorts: [{ name: 'Tier 3 — Senior Management Participants' }],
			},
			{
				id: 'tag-region-long',
				name: 'Geographic Allocation Region and Jurisdiction',
				cohorts: [
					{ name: 'Europe, Middle East and Africa (EMEA)' },
					{ name: 'United Kingdom and Crown Dependencies' },
				],
			},
		],
		shareWithInvestorSecurityValuation: true,
	},
	vehicleData: {
		pivName:
			'Meridian Global Infrastructure Co-Investment Vehicle (Cayman) Limited Partnership',
		corporateNumber: 'MC-987654321-KY-2024',
		address: {
			country: 'Cayman Islands',
			city: 'George Town',
			postCode: 'KY1-1104',
			street: 'Ugland House, South Church Street',
			houseNumber: '121',
			singleAddressString:
				'121 Ugland House, South Church Street, George Town, Grand Cayman, KY1-1104, Cayman Islands',
		},
	},
	representative: {
		firstName: 'Maximilian-Constantine',
		lastName: 'Fitzgerald-Harrington',
		email: 'maximilian.constantine.fitzgerald.harrington@legalrepresentatives.example.com',
		birthDate: new Date('1990-02-02'),
		phoneNumber: '5559876543210',
		telephonePrefix: '+352',
		address: {
			country: 'Switzerland',
			city: 'Zürich',
			postCode: 'CH-8001',
			street: 'Bahnhofstrasse, Paradeplatz District',
			houseNumber: '45',
			singleAddressString:
				'45 Bahnhofstrasse, Paradeplatz District, CH-8001 Zürich, Canton of Zürich, Switzerland',
		},
	},
	declarations: {
		isPoliticallyExposed: false,
		isBeneficialOwner: false,
		fundsSource: {
			salary: false,
			inheritance: false,
			investmentIncome: false,
			personalSavings: false,
			other: false,
		},
		// The sample investor has completed KYC, so every declaration is a real answer. A sample
		// with unanswered declarations would make the author's own preview refuse its conditions.
		answered: {
			isPoliticallyExposed: true,
			isBeneficialOwner: true,
			fundsSource: {
				salary: true,
				inheritance: true,
				investmentIncome: true,
				personalSavings: true,
				other: true,
			},
		},
	},
	holdings: [
		{
			security: {
				id: '00000000-0000-4000-8000-000000000101',
				name: 'Meridian Global Infrastructure Senior Secured Convertible Bond 2034 Series A',
				type: 'Convertible Bond',
				currency: 'USD',
				nominalValue: 1,
				votesPerUnit: 1,
				issuer: {
					name: 'Meridian Global Infrastructure Holdings International (Luxembourg) S.à r.l.',
					number: 'B-1234567890-LU-2024',
					address: {
						country: 'Luxembourg',
						city: 'Luxembourg City',
						postCode: 'L-2449',
						street: 'Boulevard Grande-Duchesse Charlotte',
						houseNumber: '124-128',
						singleAddressString:
							'124-128 Boulevard Grande-Duchesse Charlotte, L-2449 Luxembourg City, Grand Duchy of Luxembourg',
					},
				},
			},
			unitsHeld: 1000000,
			fairMarketValue: 4000000,
			costOfAcquisition: 5000000,
			votes: 1000000,
			lastTransaction: {
				id: '00000000-0000-4000-8000-0000000001a1',
				date: new Date('2023-01-15'),
				type: 'ISSUE',
				title: 'BENEFICIAL_INTEREST_AND_LEGAL_TITLE',
				units: 500000,
				cost: 2500000,
				holderRole: HolderRole.buyer,
			},
			transactions: [
				{
					date: new Date('2023-01-15'),
					units: 500000,
					cost: 2500000,
					holderRole: HolderRole.buyer,
				},
			],
		},
		{
			security: {
				id: '00000000-0000-4000-8000-000000000102',
				name: 'Meridian Renewable Energy Partnership Preferred Equity Class B Participating Shares',
				type: 'Preferred Equity',
				currency: 'EUR',
				nominalValue: 1,
				votesPerUnit: 0,
				issuer: {
					name: 'Meridian Renewable Energy Partnership Holdings (Ireland) Designated Activity Company',
					number: 'IE-987654321-2024',
					address: {
						country: 'Ireland',
						city: 'Dublin',
						postCode: 'D02 XY45',
						street: 'Sir John Rogerson’s Quay, Grand Canal Dock',
						houseNumber: '70',
						singleAddressString:
							'70 Sir John Rogerson’s Quay, Grand Canal Dock, Dublin 2, D02 XY45, Ireland',
					},
				},
			},
			unitsHeld: 2000000,
			fairMarketValue: 3000000,
			costOfAcquisition: 10000000,
			votes: 2000000,
			lastTransaction: {
				id: '00000000-0000-4000-8000-0000000001a2',
				date: new Date('2023-02-10'),
				type: 'TRANSFER',
				title: 'BENEFICIAL_INTEREST_AND_LEGAL_TITLE',
				units: 1000000,
				cost: 5000000,
				holderRole: HolderRole.buyer,
			},
			transactions: [
				{
					date: new Date('2023-02-10'),
					units: 1000000,
					cost: 5000000,
					holderRole: HolderRole.buyer,
				},
				{
					date: new Date('2023-02-10'),
					units: 1000000,
					cost: 5000000,
					holderRole: HolderRole.seller,
				},
			],
		},
	],
	allocations: [
		{
			security: {
				id: '00000000-0000-4000-8000-000000000101',
				name: 'Meridian Global Infrastructure Senior Secured Convertible Bond 2034 Series A',
				type: 'Convertible Bond',
				currency: 'USD',
				nominalValue: 1,
				votesPerUnit: 1,
				issuer: {
					name: 'Meridian Global Infrastructure Holdings International (Luxembourg) S.à r.l.',
					number: 'B-1234567890-LU-2024',
					address: {
						country: 'Luxembourg',
						city: 'Luxembourg City',
						postCode: 'L-2449',
						street: 'Boulevard Grande-Duchesse Charlotte',
						houseNumber: '124-128',
						singleAddressString:
							'124-128 Boulevard Grande-Duchesse Charlotte, L-2449 Luxembourg City, Grand Duchy of Luxembourg',
					},
				},
			},
			units: 250000,
			cost: 1250000,
			expectedType: 'ISSUE',
			targetDate: new Date('2026-04-01'),
			investmentType: 'ALLOCATION',
		},
		{
			security: {
				id: '00000000-0000-4000-8000-000000000103',
				name: 'Meridian Global Infrastructure Series C Participating Preference Shares 2034',
				type: 'Preference',
				currency: 'USD',
				nominalValue: 1,
				votesPerUnit: 0,
				issuer: {
					name: 'Meridian Global Infrastructure Holdings International (Luxembourg) S.à r.l.',
					number: 'B-1234567890-LU-2024',
					address: {
						country: 'Luxembourg',
						city: 'Luxembourg City',
						postCode: 'L-2449',
						street: 'Boulevard Grande-Duchesse Charlotte',
						houseNumber: '124-128',
						singleAddressString:
							'124-128 Boulevard Grande-Duchesse Charlotte, L-2449 Luxembourg City, Grand Duchy of Luxembourg',
					},
				},
			},
			units: 400000,
			cost: 2000000,
			expectedType: 'ISSUE',
			targetDate: new Date('2026-05-01'),
			investmentType: 'JOINER',
		},
	],
	loans: [
		{
			totalValue: 5000000,
			funder: {
				name: 'Meridian Global Infrastructure Holdings International (Luxembourg) S.à r.l.',
				number: 'B-1234567890-LU-2024',
				address: {
					country: 'Luxembourg',
					city: 'Luxembourg City',
					postCode: 'L-2449',
					street: 'Boulevard Grande-Duchesse Charlotte',
					houseNumber: '124-128',
					singleAddressString:
						'124-128 Boulevard Grande-Duchesse Charlotte, L-2449 Luxembourg City, Grand Duchy of Luxembourg',
				},
			},
		},
		{
			totalValue: 3000000,
			funder: {
				name: 'Meridian Renewable Energy Partnership Holdings (Ireland) Designated Activity Company',
				number: 'IE-987654321-2024',
				address: {
					country: 'Ireland',
					city: 'Dublin',
					postCode: 'D02 XY45',
					street: 'Sir John Rogerson’s Quay, Grand Canal Dock',
					houseNumber: '70',
					singleAddressString:
						'70 Sir John Rogerson’s Quay, Grand Canal Dock, Dublin 2, D02 XY45, Ireland',
				},
			},
		},
	],
};

/** Identifies which built-in sample smart-field dataset to render for PDF preview. */
export enum SampleSmartFieldsDataSet {
	standard = 'standard',
	long = 'long',
}

/**
 * The built-in sample datasets the generate endpoint can render when no real
 * deal (`investorId`/`mipId`) is supplied. `standard` is realistic short
 * values; `long` stress-tests layout with long values everywhere.
 */
export const SAMPLE_SMART_FIELDS_DATA_SETS: Record<
	SampleSmartFieldsDataSet,
	SmartFieldsAvailableSources
> = {
	[SampleSmartFieldsDataSet.standard]: SMART_FIELDS_SOURCES_MOCKED_DATA,
	[SampleSmartFieldsDataSet.long]: SMART_FIELDS_SOURCES_MOCKED_DATA_LONG,
};

/**
 * The one demo investor the whole field-binding surface resolves against — the bind panel's
 * per-expression samples and the whole-document render both. Two demo investors is what this
 * exists to prevent.
 */
export const SAMPLE_SOURCE_DATA: SmartFieldsAvailableSources =
	SAMPLE_SMART_FIELDS_DATA_SETS[SampleSmartFieldsDataSet.standard];

const EMPTY_ADDRESS = {
	country: null,
	city: null,
	postCode: null,
	street: null,
	houseNumber: null,
	singleAddressString: null,
};

const EMPTY_BANK_ACCOUNT = {
	bankName: null,
	country: null,
	swiftCode: null,
	accountHolder: null,
	iban: null,
	accountNumber: null,
	otherDetails: null,
};

/**
 * Every field resolves to nothing. Used to render a blank form — a Word library template added
 * to KYC, where the investor fills the gaps by hand and there is no recipient to resolve
 * against. Distinct from {@link SAMPLE_SOURCE_DATA}, whose demo values would be a real person's
 * details to anyone reading the form.
 */
export const EMPTY_SOURCE_DATA: SmartFieldsAvailableSources = {
	currentDate: new Date(0),
	deal: { projectRepresentativePerson: {} },
	mip: {
		entity: { address: { ...EMPTY_ADDRESS } },
		bankAccount: { ...EMPTY_BANK_ACCOUNT },
	},
	investor: {
		firstName: null,
		lastName: null,
		email: '',
		birthDate: null,
		phoneNumber: null,
		telephonePrefix: null,
		address: { ...EMPTY_ADDRESS },
		bankAccount: { ...EMPTY_BANK_ACCOUNT },
		additionalDetails: {},
		taxResidences: [],
		employment: [],
		nationalities: [],
		cohorts: [],
		schemes: [],
		shareWithInvestorSecurityValuation: false,
	},
	vehicleData: { address: { ...EMPTY_ADDRESS } },
	representative: { address: { ...EMPTY_ADDRESS } },
	declarations: {
		isPoliticallyExposed: false,
		isBeneficialOwner: false,
		fundsSource: {
			salary: false,
			inheritance: false,
			investmentIncome: false,
			personalSavings: false,
			other: false,
		},
		answered: {
			isPoliticallyExposed: false,
			isBeneficialOwner: false,
			fundsSource: {
				salary: false,
				inheritance: false,
				investmentIncome: false,
				personalSavings: false,
				other: false,
			},
		},
	},
	holdings: [],
	allocations: [],
	loans: [],
	customDetails: [],
};
